// SCR-01 Sign in: Face ID or fingerprint (passkey); password as fallback; device registration on first use.
import { useState } from "react";
import { Fingerprint, KeyRound, CloudOff } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../offline/db";
import { useOnline } from "../offline/useOnline";
import { sendHeldWithPasskey } from "../offline/sync";
import { callFunction } from "../lib/api";
import { Notice } from "../ui/primitives";
import { useAuth, passkeySupported } from "./AuthProvider";
import { Button, ErrorNote, Field, TextInput } from "../ui/primitives";
import { ApiError } from "../lib/api";
import { BrandScreen } from "../brand/Brand";

export function SignIn() {
  const { signInWithPasskey, signInWithPassword, canUnlockOffline, unlockOffline } = useAuth();
  const online = useOnline();
  const waiting = useLiveQuery(() => db.outbox.where("status").anyOf("waiting", "sending").count(), [], 0);
  const [heldSent, setHeldSent] = useState(false);
  const [mode, setMode] = useState<"passkey" | "password">(passkeySupported() ? "passkey" : "password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);

  const go = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) {
      setError(e instanceof ApiError ? { message: e.message, code: e.code } : { message: "Sign-in was cancelled or did not work. Try again.", code: "invalid" });
    } finally { setBusy(false); }
  };

  return (
    <BrandScreen testid="sign-in">
        {error && <ErrorNote className="mb-4" message={error.message} code={error.code} />}

        {!online && (
          <div className="mb-4 space-y-3">
            <Notice tone="warning"><CloudOff className="mr-1 inline h-4 w-4" aria-hidden />No signal. {canUnlockOffline ? "Unlock with Face ID or fingerprint to keep working; your changes are sent when signal returns." : "Sign in once with signal on this phone to be able to work offline."}</Notice>
            {canUnlockOffline && (
              <Button size="lg" block busy={busy} icon={<Fingerprint className="h-5 w-5" aria-hidden />} onClick={() => go(async () => {
                if (!(await unlockOffline())) throw new Error("not verified");
              })}>Unlock with Face ID or fingerprint</Button>
            )}
          </div>
        )}

        {online && error?.code === "invalid" && /switched off/i.test(error.message) && waiting > 0 && !heldSent && (
          <Button className="mb-4" variant="secondary" block busy={busy} onClick={() => go(async () => {
            const opts = await callFunction<{ challenge_id: string; options: any }>("passkey/login-options", {}, { auth: false });
            const response = await startAuthentication({ optionsJSON: opts.options });
            const r = await sendHeldWithPasskey({ challenge_id: opts.challenge_id, response });
            if (r.error) throw new Error(r.error);
            setHeldSent(true);
          })}>Send my {waiting} saved {waiting === 1 ? "change" : "changes"} to the CEO</Button>
        )}
        {heldSent && <Notice className="mb-4" tone="info">Your saved changes were sent and are waiting for the CEO.</Notice>}

        {mode === "passkey" ? (
          <div className="space-y-3">
            <Button size="lg" block busy={busy} icon={<Fingerprint className="h-5 w-5" aria-hidden />} onClick={() => go(signInWithPasskey)}>
              Sign in with Face ID or fingerprint
            </Button>
            <Button variant="ghost" block onClick={() => setMode("password")} icon={<KeyRound className="h-4 w-4" aria-hidden />}>
              Use my password instead
            </Button>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void go(() => signInWithPassword(email, password)); }}>
            <Field label="Email address" htmlFor="email">
              <TextInput id="email" type="email" autoComplete="username" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Password" htmlFor="password">
              <TextInput id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Button type="submit" size="lg" block busy={busy}>Sign in</Button>
            {passkeySupported() && (
              <Button variant="ghost" block onClick={() => setMode("passkey")} icon={<Fingerprint className="h-4 w-4" aria-hidden />}>
                Use Face ID or fingerprint
              </Button>
            )}
          </form>
        )}
        <p className="mt-10 text-center text-[13px] text-muted">You sign in each time you open the app. Ask the CEO if you need a login.</p>
    </BrandScreen>
  );
}

/** Shown after a password sign-in on a device without a passkey: register Face ID or fingerprint. */
export function RegisterDevicePrompt({ onDone }: { onDone: () => void }) {
  const { registerPasskey } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <BrandScreen heading={false}>
      <h1 className="text-[28px]"><Fingerprint className="mr-2 inline h-7 w-7 align-[-4px]" aria-hidden />Use Face ID or fingerprint next time</h1>
      <p className="mt-2 text-muted">Register this phone once, and sign in with a glance from now on. Your password stays as a backup.</p>
      {error && <ErrorNote className="mt-4" message={error} code="invalid" />}
      <div className="mt-6 space-y-3">
        <Button size="lg" block busy={busy} onClick={async () => {
          setBusy(true); setError(null);
          try { await registerPasskey(); onDone(); } catch (e) { setError(e instanceof ApiError ? e.message : "Registration was cancelled or did not work. Try again."); }
          finally { setBusy(false); }
        }}>Register this phone</Button>
        <Button variant="ghost" block onClick={onDone}>Not now</Button>
      </div>
    </BrandScreen>
  );
}
